import {API_ENDPOINTS, LoadingKeys} from '@/constants';
import {
  EnabledStatus,
  ExperienceItem,
  ExpPaginationAndFilterParams,
  FeaturedStatus,
  ICreateOrUpdateItem,
  IExperienceAddOrEditRequest,
  IExperienceDetails,
  IExperienceItemLite,
  ListResponse,
  LogsItem,
} from '@/models';
import {handleApiCallInStore, updateItemById} from './utils';
import {handleExperienceFilesBeforeUpload, http} from '@/utils';

import {AxiosRequestConfig} from 'axios';
import {create} from 'zustand';
import {message} from 'antd';
import {persist} from 'zustand/middleware';
import {t} from 'i18next';

export interface IExperienceStore {
  nbHits: number;
  wantedExperiencesNbHits: number;
  wantedExperiencesList: IExperienceDetails[];
  experiences: ExperienceItem[];
  experienceDetails: IExperienceDetails;
  experienceLogs: LogsItem[];
  experiencesLogs: LogsItem[];
  experiencesLite: IExperienceItemLite[];
  getAllExperiences: (params: ExpPaginationAndFilterParams) => void;
  addExpToFav: (payload: FeaturedStatus) => void;
  changeExpStatus: (payload: EnabledStatus) => void;
  getExperienceDetails: (id: string) => void;
  emptyExperienceDetails: () => void;
  addOrEditExp: (
    data: ICreateOrUpdateItem<IExperienceAddOrEditRequest> & {
      mainImageIndex: number;
    }
  ) => void;
  addExpToVibe: (payload: { id: string; isVibe: boolean }) => void;
  getExperienceLogs: (expId: string) => void;
  getExperiencesLogs: () => void;
  getAllExperiencesLite: () => void;
  getWantedExperiences: (params) => void;
}

export const useExperiencesStore = create(
  persist<IExperienceStore>(
    (set, get) => ({
      wantedExperiencesList: [],
      experiences: [],
      experienceLogs: [],
      experiencesLogs: [],
      experiencesLite: [],
      nbHits: 0,
      wantedExperiencesNbHits: 0,
      experienceDetails: undefined,
      experienceSessions: [],
      getAllExperiences: (
        params: ExpPaginationAndFilterParams = {limit: 25, page: 0}
      ) => {
        handleApiCallInStore(LoadingKeys.AllExperiences, async () => {
          const {data} = await http.get<ListResponse<ExperienceItem>>(
            API_ENDPOINTS.experiences.all,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({experiences: items ?? [], nbHits}));
        });
      },
      getWantedExperiences: params => {
        handleApiCallInStore(LoadingKeys.WantedExperiences, async () => {
          const {data} = await http.get<ListResponse<IExperienceDetails>>(
            API_ENDPOINTS.experiences.wanted,
            {params} as AxiosRequestConfig
          );
          const {items, nbHits} = data;
          set(() => ({
            wantedExperiencesList: items ?? [],
            wantedExperiencesNbHits: nbHits,
          }));
        });
      },

      addExpToFav: (payload: FeaturedStatus) => {
        handleApiCallInStore(LoadingKeys.AddExpToFav, async () => {
          const {id, isFeatured, isEnabled} = payload;
          const {experiences} = get();
          message.destroy();
          if (isFeatured) {
            if (!isEnabled) {
              message.error(`${t('notEnabledExperienceFeatureMessage')}`);
              return;
            }
            if (
              experiences.filter((exp: ExperienceItem) => exp.isFeatured)
                .length >= 15
            ) {
              message.error(`${t('maxNumberOfFeaturedExpMessage')}`);
              return;
            }
          }
          await http.put(`${API_ENDPOINTS.experiences.featured}/${id}`, {
            id,
            isFeatured,
          });
          set(({experiences}) => ({
            experiences: updateItemById(
              experiences,
              'isFeatured',
              isFeatured,
              id
            ),
          }));

          message.destroy();
          if (isFeatured) {
            message.success(t('addFeaturedExpSuccess'));
          } else {
            message.success(t('removeFeaturedExpSuccess'));
          }
        });
      },
      addExpToVibe: (payload: { id: string; isVibe: boolean }) => {
        handleApiCallInStore(LoadingKeys.AddExpToFav, async () => {
          const {id, isVibe} = payload;
          message.destroy();

          await http.put(`${API_ENDPOINTS.experiences.vibe}/${id}`, {
            id,
            isVibe,
          });
          set(({experiences}) => ({
            experiences: updateItemById(experiences, "isVibe", isVibe, id),
          }));
          message.destroy();
          if (isVibe) {
            message.success(t("addFeaturedExpSuccess"));
          } else {
            message.success(t("removeFeaturedExpSuccess"));
          }
        });
      },
      changeExpStatus: (payload: EnabledStatus) => {
        handleApiCallInStore(LoadingKeys.ChangeExpStatus, async () => {
          const {id, isEnabled} = payload;
          await http.put(`${API_ENDPOINTS.experiences.status}/${id}`, {
            id,
            isEnabled,
          });
          set(({experiences}) => ({
            experiences: updateItemById(
              experiences,
              'isEnabled',
              isEnabled,
              id
            ),
          }));

          message.destroy();
          if (isEnabled) {
            message.success(t('enableExpSuccess'));
          } else {
            message.success(t('disableExpSuccess'));
          }
        });
      },
      emptyExperienceDetails: () => {
        set(() => ({experienceDetails: undefined}));
      },
      getExperienceDetails: (expId: string) => {
        handleApiCallInStore(LoadingKeys.AllExperiences, async () => {
          const {data} = await http.get<IExperienceDetails>(
            `${API_ENDPOINTS.experiences.all}/${expId}`
          );
          set(() => ({experienceDetails: data}));
        });
      },
      addOrEditExp: ({payload, onSuccess, id, mainImageIndex}) => {
        handleApiCallInStore(LoadingKeys.AddOrEditExperience, async () => {
          const apiEndPoint = API_ENDPOINTS.experiences.addOrEditExp(id);
          const method = id ? 'put' : 'post';
          const finalPayload = await handleExperienceFilesBeforeUpload(
            payload,
            mainImageIndex
          );

          await http[method]<IExperienceAddOrEditRequest>(
            apiEndPoint,
            finalPayload as IExperienceAddOrEditRequest
          );

          await onSuccess?.();
        });
      },
      getExperienceLogs: expId => {
        handleApiCallInStore(LoadingKeys.ExperienceLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.experiences.all}?${expId}`
          );
          set(() => ({experienceLogs: data?.items}));
        });
      },
      getExperiencesLogs: () => {
        handleApiCallInStore(LoadingKeys.ExperiencesLogs, async () => {
          const {data} = await http.get<{ items: LogsItem[] }>(
            `${API_ENDPOINTS.logs}${API_ENDPOINTS.experiences.all}/export`
          );
          set(() => ({experiencesLogs: data?.items}));
        });
      },
      getAllExperiencesLite: () => {
        handleApiCallInStore(LoadingKeys.GetAllExperiencesLite, async () => {
          const {data} = await http.get<{ items: IExperienceItemLite[] }>(
            API_ENDPOINTS.experiences.allLite
          );
          set(() => ({experiencesLite: data?.items}));
        });
      },
    }),
    {
      name: 'experience-storage',

    }
  )
);
